<script>
import { useBreakpoints } from '~/composables';

export default defineComponent({
  name: 'MoleculeTabs',
  props: {
    defaultIndex: {
      default: 0,
      type: Number
    },
    resetTabs: {
      type: Boolean,
      default: false
    },
    direction: {
      type: String,
      default: 'horizontal',
      validator(value) {
        return ['horizontal', 'vertical'].includes(value);
      }
    },
    position: {
      type: String,
      default: 'left',
      validator(value) {
        return ['left', 'start', 'end', 'center'].includes(value);
      }
    },
    reverse: {
      type: Boolean,
      required: false,
      default: false
    },
    bigItem: {
      type: Boolean,
      default: false
    }
  },
  emits: {
    tabChanged(index) {
      return index !== undefined || index !== null;
    }
  },
  setup(props, { emit, slots }) {
    const { defaultIndex, resetTabs, position, direction, reverse } =
      toRefs(props);

    const selectedIndex = ref(0);
    const tabs = ref([]);
    const _tabItems = ref([]);
    const effectiveDirection = ref('horizontal');
    const { isSmallerThanSm } = useBreakpoints();

    const onTabKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (parseInt(e.key) - 1 in tabs.value) {
          e.preventDefault();
          switchTab(e, parseInt(e.key) - 1, tabs.value[parseInt(e.key) - 1]);
        }
      }
    };

    const switchTab = (_, index, isDisabled) => {
      if (!isDisabled) {
        selectedIndex.value = index;
        emit('tabChanged', index);
      }
    };

    onMounted(() => {
      selectedIndex.value = props.defaultIndex;
      getTabItems();
      document.addEventListener('keydown', onTabKeyDown);
      updateEffectiveDirection();
    });

    onBeforeUnmount(() => {
      document.removeEventListener('keydown', onTabKeyDown);
    });

    watchEffect(() => {
      updateEffectiveDirection();
    });

    watch(resetTabs, (newValue) => {
      if (newValue) {
        selectedIndex.value = props.defaultIndex;
      }
    });

    watch(defaultIndex, (newValue) => {
      if (newValue !== selectedIndex.value) {
        selectedIndex.value = newValue;
      }
    });

    watch(resetTabs, (newValue) => {
      if (newValue) {
        selectedIndex.value = props.defaultIndex;
      }
    });

    onBeforeUpdate(() => {
      getTabItems();
    });

    const getTabItems = () => {
      _tabItems.value.splice(0, _tabItems.value.length);
      slots.default().forEach((component) => {
        if (component.type.name && component.type.name === 'AtomTab') {
          _tabItems.value.push(component);
        } else if (Array.isArray(component.children)) {
          component.children.forEach((cComp) => {
            if (cComp.type.name && cComp.type.name === 'AtomTab') {
              _tabItems.value.push(cComp);
            }
          });
        }
      });
    };

    const getTitleSlotContent = (titleSlot) => {
      let slotContent = null;
      let shouldSkip = false;
      slots.default().forEach((item) => {
        if (shouldSkip) {
          return;
        }

        if (item.type === 'template' && item.props.name === titleSlot) {
          slotContent = item.children;
          shouldSkip = true;
        } else if (item.children.length) {
          item.children.forEach((cItem) => {
            if (shouldSkip) {
              return;
            }
            if (cItem.props.name === titleSlot) {
              slotContent = cItem.children;
              shouldSkip = true;
            }
          });
        }
      });
      return slotContent === null ? [] : slotContent;
    };

    const tabToDisplay = computed(() => {
      return _tabItems.value.map((item, idx) => {
        return h(
          'div',
          {
            class: 'tab',
            style: `display: ${selectedIndex.value === idx ? 'block' : 'none'}`
          },
          item
        );
      });
      // return h("div", { class: "tab" }, _tabItems.value[selectedIndex.value]);
    });

    function updateEffectiveDirection() {
      effectiveDirection.value =
        isSmallerThanSm.value && direction.value === 'vertical'
          ? 'horizontal'
          : props.direction;
    }

    return () => {
      const tabList = [];
      _tabItems.value.forEach((tab, index) => {
        const _tabProps = tab.props;

        const titleContent = _tabProps['title-slot']
          ? getTitleSlotContent(_tabProps['title-slot'])
          : _tabProps.title;
        const isDisabled =
          _tabProps.disabled === true || _tabProps.disabled === '';
        tabs.value[index] = isDisabled;

        const itemClasses = [
          'tab-list__item',
          'nobullet-list',
          props.bigItem ? 'tab-list__big-item' : ''
        ]
          .filter(Boolean)
          .join(' ');

        tabList.push(
          h(
            'li',
            {
              'class': itemClasses,
              'tabIndex': '0',
              'role': 'tabItem',
              'aria-selected': selectedIndex.value === index ? 'true' : 'false',
              'aria-disabled': isDisabled ? 'true' : 'false',
              'onClick': (e) => {
                switchTab(e, index, isDisabled);
              }
            },
            titleContent
          )
        );
      });

      return h(
        'div',
        {
          class: `tabs ${effectiveDirection.value} ${reverse.value ? 'reverse' : ''}`,
          role: 'tabs'
        },
        [
          h(
            'ul',
            { class: `tab-list ${position.value}`, role: 'tabList' },
            tabList
          ),
          ...tabToDisplay.value
        ]
      );
    };
  }
});
</script>

<style lang="scss">
.tabs {
  display: grid;
  grid-template-columns: 1fr;
  .tab-list {
    list-style: none;
    display: flex;
    padding-left: 0;
    padding-bottom: 1rem;
    border-bottom: 1px solid $color-gray-100;
    &.center {
      justify-content: center;
    }
    &.end {
      justify-content: flex-end;
    }
    &__item {
      padding: 8px 10px;
      cursor: pointer;
      user-select: none;
      transition: border 0.3s ease-in-out;
      position: relative;
      bottom: -1px;
      display: flex;
      font-weight: font-weight('semibold');

      &:not(:first-child) {
        margin-left: 10px;
      }

      &[aria-selected='true'] {
        font-weight: font-weight('semibold');
        color: $color-primary-500;
        background-color: $color-gray-200;
        box-shadow: shadow('light');
        border-bottom: 2px solid $color-secondary-500;
      }
      &[aria-disabled='true'] {
        cursor: not-allowed;
        color: var(--disabled-text-color);
      }
    }
    &__big-item {
      flex-direction: column;
      align-items: center;
      justify-content: space-around;
      width: 180px;
      height: 180px;
      border: 1px solid $color-gray-100;
      border-radius: 16px;
      &[aria-selected='true'] {
        border-bottom: none;
      }
    }
  }
  &.horizontal {
    &.reverse {
      .tab-list {
        grid-row: 2;
        border: none;
        border-top: 1px solid $color-gray-100;
      }
    }
  }

  &.vertical {
    grid-template-columns: auto 1fr;
    gap: 1rem;
    .tab-list {
      flex-direction: column;
      border-bottom: none;
      border-right: 1px solid $color-gray-100;
      min-width: 200px;

      &__item {
        margin-left: 0;
        border-radius: 0;
        display: flex;
        align-items: center;

        &[aria-selected='true'] {
          border: none;
          border-left: 2px solid $color-secondary-500;
        }
      }
    }

    &.reverse {
      grid-template-columns: 1fr auto;
      .tab-list {
        grid-column: 2;
        border: none;
        border-left: 1px solid $color-gray-100;
      }

      .tab {
        grid-row: 1;
        grid-column: 1;
      }
    }
  }
}

ul > li:before {
  content: '';
}
</style>
